import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

export interface DividendPayment {
  id: string;
  ticker: string;
  companyName: string;
  amount: number;
  currency: string;
  paymentDate: string;
  type: 'dividend' | 'interest';
  logo?: string;
}

export interface DividendSummary {
  totalDividendsAllTime: number;
  totalDividendsCurrentYear: number;
  totalDividendsCurrentMonth: number;
  totalInterestAllTime: number;
  totalInterestCurrentYear: number;
  totalInterestCurrentMonth: number;
  dividendsByYear: { [year: string]: number };
  dividendsByMonth: { [month: string]: number };
  interestByYear: { [year: string]: number };
  interestByMonth: { [month: string]: number };
  portfolioWeightedDividendYield: number;
  portfolioYieldOnCost: number;
  selectedYear?: number;
}

export interface HoldingDividendInfo {
  ticker: string;
  companyName: string;
  shares: number;
  currentPrice: number;
  averageCost: number;
  currentDividendYield: number;
  yieldOnCost: number;
  annualDividendIncome: number;
  logo: string;
}

export interface YearlyData {
  year: number;
  totalDividends: number;
  totalInterest: number;
  totalPayments: number;
  topCompanies: Array<{
    ticker: string;
    companyName: string;
    amount: number;
    logo: string;
  }>;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalPayments: number;
  limit: number;
}

export interface DividendCompanyStats {
  ticker: string;
  companyName: string;
  totalAmount: number;
  paymentCount: number;
  firstPaymentDate: string;
  lastPaymentDate: string;
  logo?: string;
}

export interface DividendStatsResponse {
  companies: DividendCompanyStats[];
  pagination: PaginationInfo;
  totalStats: {
    totalCompanies: number;
    totalAmountAllCompanies: number;
    totalPaymentCount: number;
  };
}

export interface TopDividendHolding {
  ticker: string;
  companyName: string;
  shares: number;
  currentPrice: number;
  currentValue: number;
  dividendYield: number;
  annualDividendIncome: number;
  quarterlyDividendIncome: number;
  monthlyDividendIncome: number;
  logo?: string;
}

export interface TopDividendHoldingsResponse {
  holdings: TopDividendHolding[];
  pagination: PaginationInfo;
  totalStats: {
    totalHoldings: number;
    totalAnnualDividendIncome: number;
    totalCurrentValue: number;
    weightedAverageDividendYield: number;
  };
}

export interface PortfolioDividendStats {
  totalDividendsAllTime: number;
  totalInterestAllTime: number;
  portfolioWeightedDividendYield: number;
  portfolioYieldOnCost: number;
  portfolioCurrentValue: number;
  dividendPayingHoldingsCount: number;
  totalHoldingsCount: number;
}

export interface DividendAnalysisResponse {
  summary: DividendSummary;
  payments: DividendPayment[];
  holdings: HoldingDividendInfo[];
  yearlyData?: YearlyData[];
  pagination?: PaginationInfo;
}

export function useDividendAnalysis(
  year?: number | 'all', 
  mode: 'year' | 'graph' = 'year',
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'paymentDate',
  sortOrder: 'asc' | 'desc' = 'desc'
) {
  return useQuery<DividendAnalysisResponse, Error>({
    queryKey: ["dividendAnalysis", year, mode, page, limit, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (year !== undefined) {
        params.append('year', year.toString());
      }
      if (mode !== 'year') {
        params.append('mode', mode);
      }
      if (page !== 1) {
        params.append('page', page.toString());
      }
      if (limit !== 10) {
        params.append('limit', limit.toString());
      }
      if (sortBy !== 'paymentDate') {
        params.append('sortBy', sortBy);
      }
      if (sortOrder !== 'desc') {
        params.append('sortOrder', sortOrder);
      }
      
      const queryString = params.toString();
      const url = `/portfolio/dividend-analysis${queryString ? `?${queryString}` : ''}`;
      
      const { data } = await api.get<DividendAnalysisResponse>(url);
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh dividend analysis
    gcTime: 30 * 60 * 1000, // 30 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  });
}

export function useDividendStats(
  page: number = 1,
  limit: number = 25
) {
  return useQuery<DividendStatsResponse, Error>({
    queryKey: ["dividendStats", page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (page !== 1) {
        params.append('page', page.toString());
      }
      if (limit !== 25) {
        params.append('limit', limit.toString());
      }
      
      const queryString = params.toString();
      const url = `/portfolio/dividend-stats${queryString ? `?${queryString}` : ''}`;
      
      const { data } = await api.get<DividendStatsResponse>(url);
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh dividend stats
    gcTime: 30 * 60 * 1000, // 30 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  });
}

export function useTopDividendHoldings(
  page: number = 1,
  limit: number = 10
) {
  return useQuery<TopDividendHoldingsResponse, Error>({
    queryKey: ["topDividendHoldings", page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (page !== 1) {
        params.append('page', page.toString());
      }
      if (limit !== 10) {
        params.append('limit', limit.toString());
      }
      
      const queryString = params.toString();
      const url = `/portfolio/top-dividend-holdings${queryString ? `?${queryString}` : ''}`;
      
      const { data } = await api.get<TopDividendHoldingsResponse>(url);
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh dividend holdings
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  });
}

export function usePortfolioDividendStats() {
  return useQuery<PortfolioDividendStats, Error>({
    queryKey: ["portfolioDividendStats"],
    queryFn: async () => {
      const { data } = await api.get<PortfolioDividendStats>('/portfolio/portfolio-dividend-stats');
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh portfolio dividend stats
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  });
}

export interface DividendCalendarEvent {
  ticker: string;
  companyName: string;
  logo: string;
  estimatedDate: string;
  estimatedAmount: number;
  actualPaymentDate?: string;
  frequency: string;
  growthRate: number;
  isEstimate: boolean;
}

export interface DividendCalendarResponse {
  events: DividendCalendarEvent[];
  stats: {
    totalEstimatedIncome: number;
    monthlyBreakdown: { [month: string]: number };
  };
}

export function useDividendCalendar() {
  return useQuery<DividendCalendarResponse, Error>({
    queryKey: ["dividendCalendar"],
    queryFn: async () => {
      const { data } = await api.get<DividendCalendarResponse>('/portfolio/dividend-calendar');
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh dividend calendar
    gcTime: 30 * 60 * 1000, // 30 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  });
}